import React, { useEffect, useMemo, useState } from "react";
import {
  describeSlotForUser, nextOccurrence,
  getUserTz, getUserTzAbbr, LIVE_SOURCE_TZ,
} from "../flexxTime";
import {
  T, TYPE_COLOR, TYPE_ICON, DAYS_OF_WEEK, STATUS_PILL_COLOR,
  ensureSessionId, groupSessionsByDay, dateForWeekday, resolveDaySessions,
  sectionTitle, card, input, btn, muteHint,
} from "./hubShared";

// ── Public: Weekly Schedule + Countdown ─────────────────────────────
export function WeeklySchedule({ schedule, overrides }) {
  const userTz = getUserTz();
  const userTzAbbr = getUserTzAbbr();
  const computeNext = useMemo(() => () => nextSessionLocal(schedule, overrides), [schedule, overrides]);
  const [next, setNext] = useState(computeNext);
  const [countdown, setCountdown] = useState(() => formatCountdown(next.ms));
  useEffect(() => {
    const i = setInterval(() => {
      const n = computeNext();
      setNext(n);
      setCountdown(formatCountdown(n.ms));
    }, 1000);
    return () => clearInterval(i);
  }, [computeNext]);
  return (
    <section data-testid="hub-schedule-section" style={{ marginBottom: 24 }}>
      <h2 style={sectionTitle}>📅 TRAINING SCHEDULE</h2>
      <div data-testid="hub-next-session-countdown" style={{
        background: `linear-gradient(135deg, ${T.gold}15 0%, ${T.gold}05 100%)`,
        border: `1px solid ${T.gold}40`, borderRadius: 8,
        padding: "12px 16px", marginBottom: 14, display: "flex",
        justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: T.gold, fontFamily: "monospace" }}>NEXT SESSION · YOUR TIME ({userTzAbbr})</div>
          <div data-testid="hub-next-session-label" style={{ color: T.text, fontSize: 14, marginTop: 4, fontWeight: 600 }}>{next.label}</div>
        </div>
        <div data-testid="hub-countdown" style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 22, fontWeight: 800, color: T.gold, letterSpacing: 1 }}>
          {countdown}
        </div>
      </div>
      <div style={{ color: T.mute, fontSize: 11, fontFamily: "monospace", letterSpacing: 0.3, marginBottom: 14, paddingLeft: 2 }}>
        🌐 Sessions hosted in PHT · shown in your local time ({userTz}, {userTzAbbr})
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {groupSessionsByDay(schedule).map(({ day, sessions: baseSessions }) => {
          const dateStr = dateForWeekday(day);
          const sessions = resolveDaySessions(baseSessions, overrides, dateStr);
          const dayProbe = describeSlotForUser(day, "12:00", LIVE_SOURCE_TZ);
          const hasAnyScheduled = sessions.some((s) => (s.status || "scheduled") === "scheduled");
          const isToday = dayProbe.sameDay && hasAnyScheduled;
          const dayLabel = dayProbe.localDay || day.slice(0, 3);
          const dateLabel = (() => {
            try {
              const [, m, d] = (dateStr || "").split("-").map(Number);
              const mmm = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1] || "";
              return d ? `${mmm} ${d}` : "";
            } catch (_) { return ""; }
          })();

          if (sessions.length === 0 || (!hasAnyScheduled && sessions.every((s) => s.status === "none"))) {
            const note = sessions.find((s) => s.note)?.note;
            return (
              <div key={day} data-testid={`hub-schedule-card-${day.toLowerCase()}`} data-status="empty"
                style={card({ borderColor: `${T.mute}55`, borderLeft: `3px solid ${T.mute}`, padding: 14, background: "#0a0e13" })}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ color: T.text, fontWeight: 700, fontSize: 15, opacity: 0.6 }}>
                    {dayLabel}
                    {dateLabel && <span style={{ color: T.mute, fontSize: 11, fontWeight: 400, marginLeft: 6, fontFamily: "monospace" }}>· {dateLabel}</span>}
                  </div>
                  <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: `${T.mute}22`, color: T.mute, fontWeight: 800, fontFamily: "monospace", letterSpacing: 1.5, border: `1px solid ${T.mute}55` }}>NO SESSION</span>
                </div>
                <div style={{ color: T.mute, fontSize: 12, fontStyle: "italic" }}>
                  {note ? `💤 ${note}` : "Rest day. Use the time to journal and review."}
                </div>
              </div>
            );
          }

          return (
            <div key={day} data-testid={`hub-schedule-card-${day.toLowerCase()}`} data-status="multi"
              style={card({ borderColor: isToday ? T.gold : T.border, boxShadow: isToday ? `0 0 24px ${T.goldGlow}` : "none", padding: 14 })}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ color: isToday ? T.gold : T.text, fontWeight: 700, fontSize: 15, letterSpacing: 0.5 }}>
                  {dayLabel}
                  {dateLabel && <span style={{ color: T.mute, fontSize: 11, fontWeight: 400, marginLeft: 6, fontFamily: "monospace" }}>· {dateLabel}</span>}
                </div>
                {isToday && (
                  <span data-testid="hub-today-badge" style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: T.gold, color: "#000", fontWeight: 800, fontFamily: "monospace", letterSpacing: 1 }}>TODAY</span>
                )}
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {sessions.map((s, idx) => <SessionRow key={s.id || idx} day={day} s={s} />)}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SessionRow({ day, s }) {
  const status = s.status || "scheduled";
  const slot = describeSlotForUser(day, s.time_pht, LIVE_SOURCE_TZ);
  const tColor = TYPE_COLOR[s.type] || T.gold;
  if (status === "none") return null;
  const isCancel = status === "cancelled";
  const accent = isCancel ? "#FF4444" : tColor;
  return (
    <div data-testid={`hub-session-${s.id || `${day.toLowerCase()}-${s.time_pht || ""}`}`} data-status={status}
      style={{ padding: 10, background: "#0a0e13", borderLeft: `3px solid ${accent}`, border: `1px solid ${accent}33`, borderRadius: 6, opacity: isCancel ? 0.78 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, gap: 6, flexWrap: "wrap" }}>
        <div style={{ color: isCancel ? T.mute : T.gold, fontSize: 13, fontFamily: "monospace", fontWeight: 700, textDecoration: isCancel ? "line-through" : "none" }}>
          {slot.localTime}
        </div>
        {isCancel && (
          <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 3, background: `${accent}22`, color: accent, fontWeight: 800, fontFamily: "monospace", letterSpacing: 1.2, border: `1px solid ${accent}55` }}>CANCELLED</span>
        )}
      </div>
      <div style={{ color: isCancel ? T.mute : T.text, fontSize: 12.5, lineHeight: 1.4, textDecoration: isCancel ? "line-through" : "none" }}>
        <span style={{ marginRight: 5 }}>{TYPE_ICON[s.type] || "📺"}</span>{s.title}
      </div>
      {s.description && !isCancel && <div style={{ color: T.mute, fontSize: 11, lineHeight: 1.5, marginTop: 4 }}>{s.description}</div>}
      {isCancel && s.note && <div style={{ color: T.text, fontSize: 11, lineHeight: 1.5, marginTop: 4, fontStyle: "italic" }}>❌ {s.note}</div>}
      <div style={{ marginTop: 5, fontSize: 8.5, color: tColor, fontFamily: "monospace", letterSpacing: 1.4 }}>{s.type}</div>
    </div>
  );
}

function nextSessionLocal(schedule, overrides) {
  const now = Date.now();
  let bestMs = Infinity, bestLabel = "—";
  const cancelKey = (d, sid) => `${d}::${sid}`;
  const cancelSet = new Set();
  const addOverrides = [];
  for (const ov of overrides || []) {
    if (ov.type === "cancel" && ov.session_id && ov.date) cancelSet.add(cancelKey(ov.date, ov.session_id));
    else if (ov.type === "add" && ov.date) addOverrides.push(ov);
  }
  for (const slot of schedule || []) {
    if (slot.status && slot.status !== "scheduled") continue;
    const occ = nextOccurrence(slot.day, slot.time_pht, LIVE_SOURCE_TZ);
    if (!occ) continue;
    const occDate = occ.toISOString().slice(0, 10);
    if (cancelSet.has(cancelKey(occDate, slot.id))) continue;
    const diff = occ.getTime() - now;
    if (diff > 0 && diff < bestMs) {
      bestMs = diff;
      const local = describeSlotForUser(slot.day, slot.time_pht, LIVE_SOURCE_TZ);
      bestLabel = `${local.localDay} · ${local.localTime} · ${slot.title}`;
    }
  }
  for (const ov of addOverrides) {
    try {
      const [y, m, d] = ov.date.split("-").map(Number);
      const [hh, mm] = (ov.time_pht || "00:00").split(":").map(Number);
      const utcMs = Date.UTC(y, m - 1, d, hh, mm) - 8 * 3600 * 1000;
      const diff = utcMs - now;
      if (diff > 0 && diff < bestMs) {
        bestMs = diff;
        const dt = new Date(utcMs);
        const localDay = dt.toLocaleDateString(undefined, { weekday: "short" });
        const localTime = dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
        bestLabel = `${localDay} · ${localTime} · ${ov.title || "Bonus"}`;
      }
    } catch (_) { /* ignore */ }
  }
  return { ms: bestMs === Infinity ? 0 : bestMs, label: bestLabel };
}

function formatCountdown(ms) {
  if (!ms || ms < 0) return "00:00:00:00";
  const pad = (n) => String(n).padStart(2, "0");
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${pad(d)}d ${pad(h)}h ${pad(m)}m ${pad(s)}s`;
}

// ── Admin: Schedule editor (multi-session/day + per-date overrides) ──
export function AdminScheduleEditor({ state, onUpdate }) {
  const [rows, setRows] = useState(() => (state.schedule || []).map(ensureSessionId));
  const [overrides, setOverrides] = useState(() => state.overrides || []);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const SESSION_TYPES = ["FOREX", "CRYPTO", "INDICES", "AI TOOLS"];
  const STATUS_OPTS = [
    { v: "scheduled", label: "✅ Scheduled" },
    { v: "cancelled", label: "❌ Cancelled" },
    { v: "none", label: "— Skip" },
  ];

  const updateRow = (id, patch) => setRows((r) => r.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  const addSessionToDay = (day) => {
    const id = `s-${Math.random().toString(36).slice(2, 10)}`;
    setRows((r) => [...r, { id, day, time_pht: "21:00", title: "New Session", type: "FOREX", status: "scheduled", note: "", description: "" }]);
  };
  const deleteSession = (id) => setRows((r) => r.filter((row) => row.id !== id));
  const save = async () => {
    setBusy(true);
    try { await onUpdate({ schedule: rows, overrides }); } finally { setBusy(false); }
  };
  const reset = () => {
    setRows((state.schedule || []).map(ensureSessionId));
    setOverrides(state.overrides || []);
  };

  const formatTelegram = () => {
    const grouped = groupSessionsByDay(rows);
    const lines = ["📅 *FLEXX ACADEMY — TRAINING SCHEDULE*", "_All times in PHT (GMT+8)_", ""];
    for (const { day, sessions: base } of grouped) {
      const dateStr = dateForWeekday(day);
      const sessions = resolveDaySessions(base, overrides, dateStr);
      const cancelled = sessions.filter((s) => s.status === "cancelled");
      const scheduled = sessions.filter((s) => (s.status || "scheduled") === "scheduled");
      const skipped = sessions.filter((s) => s.status === "none");
      const headerDate = (() => {
        try {
          const [, m, d] = (dateStr || "").split("-").map(Number);
          const mmm = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1] || "";
          return d ? ` (${mmm} ${d})` : "";
        } catch (_) { return ""; }
      })();
      if (sessions.length === 0) {
        lines.push(`💤 *${day.toUpperCase()}*${headerDate} — No sessions`);
      } else if (scheduled.length === 0 && (cancelled.length > 0 || skipped.length > 0)) {
        const reason = (cancelled[0]?.note || skipped[0]?.note || "");
        lines.push(`❌ *${day.toUpperCase()}*${headerDate} — No sessions${reason ? ` (${reason})` : ""}`);
      } else {
        lines.push(`🟡 *${day.toUpperCase()}*${headerDate}`);
        for (const s of scheduled) {
          const tag = s._is_one_off ? " 🆕" : "";
          lines.push(`  •${tag} ${s.time_pht || "TBD"} — ${s.title || "Session"} (${s.type || "FOREX"})`);
        }
        for (const s of cancelled) {
          lines.push(`  ❌ ${s.time_pht || "TBD"} — ~${s.title || "Session"}~ — CANCELLED${s.note ? ` (${s.note})` : ""}`);
        }
      }
      lines.push("");
    }
    lines.push("Join: courses.flexx-academy.com/library");
    return lines.join("\n");
  };
  const copyToClipboard = async () => {
    const text = formatTelegram();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { window.prompt("Copy this:", text); }
  };

  const grouped = groupSessionsByDay(rows);

  return (
    <div data-testid="hub-admin-schedule-editor">
      <div style={{ ...muteHint, marginBottom: 10 }}>
        Add multiple sessions per day. Mark a row <span style={{ color: "#FF4444" }}> Cancelled</span> when a host can't make it,
        or <span style={{ color: T.mute }}>Skip</span> to remove the slot. Click <b>Copy for Telegram</b> to share.
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <button data-testid="hub-schedule-copy-telegram-btn" onClick={copyToClipboard}
          style={btn({ active: copied, color: copied ? "#22C55E" : T.gold })}>
          {copied ? "✓ Copied!" : "📋 Copy for Telegram"}
        </button>
      </div>

      <div style={{ display: "grid", gap: 14 }}>
        {grouped.map(({ day, sessions }) => (
          <div key={day} style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, background: "#0a0e13" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontFamily: "monospace", color: T.gold, letterSpacing: 1.5, fontWeight: 800 }}>
                {day.toUpperCase()} <span style={{ color: T.mute, fontWeight: 400, fontSize: 10, marginLeft: 8 }}>· {sessions.length} session{sessions.length === 1 ? "" : "s"}</span>
              </div>
              <button data-testid={`hub-schedule-add-${day.toLowerCase()}`} onClick={() => addSessionToDay(day)} style={btn({ active: false, color: T.gold })}>+ Add session</button>
            </div>
            {sessions.length === 0 ? (
              <div style={{ color: T.mute, fontSize: 12, fontStyle: "italic", padding: "10px 4px" }}>
                No sessions for {day}. Click <b>+ Add session</b>.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {sessions.map((row) => {
                  const status = row.status || "scheduled";
                  const dim = status !== "scheduled";
                  const statusColor = STATUS_PILL_COLOR[status] || T.gold;
                  return (
                    <div key={row.id} style={{ padding: 10, background: T.panel, border: `1px solid ${dim ? `${statusColor}55` : T.border}`, borderLeft: `3px solid ${statusColor}`, borderRadius: 8, opacity: dim ? 0.85 : 1 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 130px 1.4fr 36px", gap: 8, alignItems: "center" }}>
                        <input value={row.time_pht || ""} onChange={(e) => updateRow(row.id, { time_pht: e.target.value })} placeholder="21:00" style={input} disabled={status === "none"} />
                        <input value={row.title || ""} onChange={(e) => updateRow(row.id, { title: e.target.value })} placeholder="Session title" style={input} disabled={status === "none"} />
                        <select value={row.type || "FOREX"} onChange={(e) => updateRow(row.id, { type: e.target.value })} style={{ ...input, padding: "8px 8px" }} disabled={status === "none"}>
                          {SESSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <input value={row.description || ""} onChange={(e) => updateRow(row.id, { description: e.target.value })} placeholder="Optional description" style={input} disabled={status === "none"} />
                        <button onClick={() => deleteSession(row.id)} title="Delete" style={{ ...btn({ active: false, color: "#FF6B35" }), padding: "8px 10px" }}>🗑️</button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8, alignItems: "center" }}>
                        <select value={status} onChange={(e) => updateRow(row.id, { status: e.target.value })} style={{ ...input, padding: "8px 8px", color: statusColor, borderColor: `${statusColor}55` }}>
                          {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                        </select>
                        <input value={row.note || ""} onChange={(e) => updateRow(row.id, { note: e.target.value })} placeholder={status === "cancelled" ? "Reason" : "Internal note (optional)"} style={input} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 18 }}>
        <OverridesEditor rows={rows} overrides={overrides} setOverrides={setOverrides} sessionTypes={SESSION_TYPES} />
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
        <button data-testid="hub-schedule-reset-btn" onClick={reset} style={btn({ active: false, color: T.mute })}>Reset</button>
        <button data-testid="hub-schedule-save-btn" onClick={save} disabled={busy} style={btn({ active: true, color: T.gold })}>{busy ? "Saving…" : "Save All"}</button>
      </div>
    </div>
  );
}

function OverridesEditor({ rows, overrides, setOverrides, sessionTypes }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const SESSION_TYPES = sessionTypes;
  const upcoming = (overrides || []).filter((o) => (o.date || "") >= todayStr).sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.time_pht || "").localeCompare(b.time_pht || ""));
  const pastCount = (overrides || []).length - upcoming.length;

  const addCancel = () => {
    const firstSession = (rows || []).find((r) => (r.status || "scheduled") === "scheduled");
    if (!firstSession) { window.alert("Add a recurring session first."); return; }
    const tmrw = new Date(); tmrw.setDate(tmrw.getDate() + 1);
    const id = `ov-${Math.random().toString(36).slice(2, 10)}`;
    setOverrides([...(overrides || []), { id, type: "cancel", session_id: firstSession.id, date: tmrw.toISOString().slice(0, 10), note: "" }]);
  };
  const addOneOff = () => {
    const tmrw = new Date(); tmrw.setDate(tmrw.getDate() + 1);
    const id = `ov-${Math.random().toString(36).slice(2, 10)}`;
    setOverrides([...(overrides || []), { id, type: "add", date: tmrw.toISOString().slice(0, 10), time_pht: "21:00", title: "Bonus Session", session_type: "FOREX", note: "" }]);
  };
  const updateOv = (id, patch) => setOverrides((overrides || []).map((o) => (o.id === id ? { ...o, ...patch } : o)));
  const delOv = (id) => setOverrides((overrides || []).filter((o) => o.id !== id));
  const cleanPast = () => {
    if (!window.confirm(`Delete ${pastCount} past override${pastCount === 1 ? "" : "s"}?`)) return;
    setOverrides((overrides || []).filter((o) => (o.date || "") >= todayStr));
  };
  const sessionLabel = (sid) => {
    const s = (rows || []).find((r) => r.id === sid);
    if (!s) return "— deleted recurring session —";
    return `${s.day} ${s.time_pht || ""} · ${s.title || ""}`.trim();
  };

  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, background: "#0a0e13" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 11, letterSpacing: 1.5, fontWeight: 800 }}>📌 PER-DATE OVERRIDES</div>
          <div style={{ ...muteHint, marginTop: 4 }}>Cancel one date or add a bonus session — without touching the weekly template.</div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={addCancel} style={btn({ active: false, color: "#FF6B35" })}>+ Cancel a date</button>
          <button onClick={addOneOff} style={btn({ active: false, color: T.gold })}>+ Add one-off</button>
          {pastCount > 0 && <button onClick={cleanPast} style={btn({ active: false, color: T.mute })}>Clean past ({pastCount})</button>}
        </div>
      </div>
      {upcoming.length === 0 ? (
        <div style={{ padding: 12, color: T.mute, fontSize: 12, fontStyle: "italic", textAlign: "center", border: `1px dashed ${T.border}`, borderRadius: 8, marginTop: 8 }}>
          No upcoming overrides. The weekly schedule runs as-is.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
          {upcoming.map((ov) => {
            const isCancel = ov.type === "cancel";
            const accent = isCancel ? "#FF4444" : T.gold;
            return (
              <div key={ov.id} style={{ padding: 10, background: T.panel, borderLeft: `3px solid ${accent}`, border: `1px solid ${accent}33`, borderRadius: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "100px 130px 1fr 36px", gap: 8, alignItems: "center" }}>
                  <span style={{ padding: "3px 8px", borderRadius: 4, background: `${accent}22`, color: accent, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5, fontWeight: 800, textAlign: "center", border: `1px solid ${accent}55` }}>
                    {isCancel ? "CANCEL" : "ADD"}
                  </span>
                  <input type="date" value={ov.date || ""} onChange={(e) => updateOv(ov.id, { date: e.target.value })} min={todayStr} style={input} />
                  {isCancel ? (
                    <select value={ov.session_id || ""} onChange={(e) => updateOv(ov.id, { session_id: e.target.value })} style={{ ...input, padding: "8px 8px" }}>
                      {(rows || []).map((s) => <option key={s.id} value={s.id}>{s.day} {s.time_pht || ""} · {s.title || "(untitled)"}</option>)}
                    </select>
                  ) : (
                    <input value={ov.title || ""} onChange={(e) => updateOv(ov.id, { title: e.target.value })} placeholder="One-off session title" style={input} />
                  )}
                  <button onClick={() => delOv(ov.id)} title="Remove" style={{ ...btn({ active: false, color: "#FF6B35" }), padding: "8px 10px" }}>🗑️</button>
                </div>
                {!isCancel && (
                  <div style={{ display: "grid", gridTemplateColumns: "100px 1fr 130px", gap: 8, marginTop: 8, alignItems: "center" }}>
                    <input value={ov.time_pht || ""} onChange={(e) => updateOv(ov.id, { time_pht: e.target.value })} placeholder="21:00" style={input} />
                    <input value={ov.note || ""} onChange={(e) => updateOv(ov.id, { note: e.target.value })} placeholder="Description (optional)" style={input} />
                    <select value={ov.session_type || "FOREX"} onChange={(e) => updateOv(ov.id, { session_type: e.target.value })} style={{ ...input, padding: "8px 8px" }}>
                      {SESSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
                {isCancel && (
                  <div style={{ marginTop: 8 }}>
                    <input value={ov.note || ""} onChange={(e) => updateOv(ov.id, { note: e.target.value })} placeholder="Reason (e.g. Host travelling)" style={input} />
                    <div style={{ marginTop: 4, color: T.mute, fontSize: 10, fontStyle: "italic" }}>Cancels: {sessionLabel(ov.session_id)}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
